import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CATALOG_PRODUCTS,
  CATEGORIES,
  findProduct,
  defaultVariant,
  cartKey,
  formatPrice,
  discountPct,
} from "../data/products.js";
import { useCart } from "../context/CartContext.jsx";
import ProductCard from "../components/ProductCard.jsx";
import ProductImage from "../components/ProductImage.jsx";
import Seo from "../components/Seo.jsx";

const ORIGIN = "https://rimmonfarm.com";
const WISHLIST_KEY = "rf_wishlist_v1";

const readWishlist = () => {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
  } catch {
    return [];
  }
};

// Single-product structured data for the detail page, mirroring the shape the
// catalog-level ItemList uses so Google sees consistent offers.
function useProductJsonLd(product) {
  useEffect(() => {
    if (!product) return;
    const data = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.desc,
      image: `${ORIGIN}${product.fallback}`,
      category: product.category,
      brand: { "@type": "Brand", name: "Rimmon Farm" },
      offers: product.variants?.length
        ? {
            "@type": "AggregateOffer",
            lowPrice: Math.min(...product.variants.map((v) => v.price)),
            highPrice: Math.max(...product.variants.map((v) => v.price)),
            offerCount: product.variants.length,
            priceCurrency: "INR",
            availability: "https://schema.org/InStock",
            url: `${ORIGIN}/products/${product.id}`,
            seller: { "@type": "Organization", name: "Rimmon Farm" },
          }
        : {
            "@type": "Offer",
            price: product.pricePerKg,
            priceCurrency: "INR",
            availability: "https://schema.org/InStock",
            url: `${ORIGIN}/products/${product.id}`,
            seller: { "@type": "Organization", name: "Rimmon Farm" },
          },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-jsonld", "product-detail");
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
    return () => el.remove();
  }, [product]);
}

/* Inline icons keep the trust row and action buttons dependency-free. */
const HeartIcon = ({ filled }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21.2l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.8z" />
  </svg>
);

const ShareIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" />
  </svg>
);

const ThumbIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.3a2 2 0 0 0 2-1.7l1.4-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const TruckIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" />
    <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

const TRUST_BADGES = [
  { id: "quality", icon: <ShieldIcon />, label: "100% Farm Fresh" },
  { id: "satisfaction", icon: <ThumbIcon />, label: "Satisfaction Guaranteed" },
  { id: "secure", icon: <LockIcon />, label: "Secure Checkout" },
];

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = findProduct(id);
  const { items, add, remove } = useCart();

  const variants = product?.variants || null;
  const [sizeId, setSizeId] = useState(() => defaultVariant(product)?.id || null);

  // Reset the selected size when navigating between two oils' detail pages —
  // the component instance is reused, so state doesn't reset on its own.
  useEffect(() => {
    setSizeId(defaultVariant(product)?.id || null);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const variant = variants ? variants.find((v) => v.id === sizeId) || variants[0] : null;

  // Gallery: main photo + extra images (if any) or fallback artwork + every pack-size artwork, deduped.
  const gallery = useMemo(() => {
    if (!product) return [];
    const stills = product.images?.length ? product.images : [product.fallback];
    return Array.from(
      new Set(
        [product.photo, ...stills, ...(variants?.map((v) => v.image) || [])].filter(Boolean)
      )
    );
  }, [product, variants]);

  const [imgIdx, setImgIdx] = useState(0);
  useEffect(() => setImgIdx(0), [id]);
  // Selecting a pack size jumps the gallery to that size's artwork.
  useEffect(() => {
    if (!variant?.image) return;
    const i = gallery.indexOf(variant.image);
    if (i >= 0) setImgIdx(i);
  }, [variant?.image, gallery]);

  // Auto-advance the gallery like a slideshow when there's more than one photo.
  // Pauses for a while after the shopper manually picks a thumbnail.
  const [autoplay, setAutoplay] = useState(true);
  useEffect(() => {
    if (!autoplay || gallery.length < 2) return;
    const t = setInterval(() => setImgIdx((i) => (i + 1) % gallery.length), 3000);
    return () => clearInterval(t);
  }, [autoplay, gallery.length]);

  const selectImage = (i) => {
    setImgIdx(i);
    setAutoplay(false);
    setTimeout(() => setAutoplay(true), 8000);
  };

  const [wished, setWished] = useState(() => readWishlist().includes(id));
  useEffect(() => setWished(readWishlist().includes(id)), [id]);

  const [shared, setShared] = useState(false);
  const [pin, setPin] = useState("");
  const [pinStatus, setPinStatus] = useState(null); // null | "ok" | "bad"
  useEffect(() => {
    setPin("");
    setPinStatus(null);
  }, [id]);

  useProductJsonLd(product);

  if (!product) {
    return (
      <section className="section">
        <div className="container pdp-missing">
          <Seo title="Product not found" path={`/products/${id}`} noindex />
          <h1>Product not found</h1>
          <p>This item may have been delisted or the link is wrong.</p>
          <Link className="btn btn--primary" to="/products">Browse all products</Link>
        </div>
      </section>
    );
  }

  const key = cartKey(product.id, variant?.id);
  const qty = items[key] || 0;
  const price = variant?.price ?? product.pricePerKg;
  const mrp = variant?.mrp ?? product.mrp;
  const off = discountPct(price, mrp);
  const unit = variant?.unit || product.unit;
  const categoryLabel = CATEGORIES.find((c) => c.id === product.category)?.label || "Products";

  const related = CATALOG_PRODUCTS.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  const toggleWishlist = () => {
    const list = readWishlist();
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    setWished(next.includes(id));
  };

  const share = async () => {
    const url = `${ORIGIN}/products/${product.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: product.name, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      }
    } catch {
      /* user dismissed the share sheet */
    }
  };

  const buyNow = () => {
    if (qty === 0) add(key);
    navigate("/checkout");
  };

  const checkPin = (e) => {
    e.preventDefault();
    setPinStatus(/^[1-9]\d{5}$/.test(pin.trim()) ? "ok" : "bad");
  };

  return (
    <>
      <Seo
        title={`${product.name} — Specifications & Price`}
        description={product.desc}
        path={`/products/${product.id}`}
      />

      <section className="section pdp">
        <div className="container">
          <nav className="pdp__crumbs" aria-label="Breadcrumb">
            <Link to="/products">Shop</Link>
            <span aria-hidden="true">›</span>
            <Link to={`/products?category=${product.category}`}>{categoryLabel}</Link>
            <span aria-hidden="true">›</span>
            <span>{product.name}</span>
          </nav>

          <div className="pdp__grid">
            <div className="pdp__gallery">
              {gallery.length > 1 && (
                <div className="pdp__thumbs" role="group" aria-label="Product photos">
                  {gallery.map((src, i) => (
                    <button
                      key={src}
                      type="button"
                      className={`pdp__thumb ${i === imgIdx ? "is-active" : ""}`}
                      onClick={() => selectImage(i)}
                      aria-label={`Photo ${i + 1} of ${product.name}`}
                      aria-pressed={i === imgIdx}
                    >
                      <ProductImage product={product} override={src} />
                    </button>
                  ))}
                </div>
              )}
              <div className="pdp__media">
                {product.badge && <span className="pdp__badge">{product.badge}</span>}
                <ProductImage product={product} className="pdp__img" override={gallery[imgIdx]} />
              </div>
            </div>

            <div className="pdp__info">
              <h1>{product.name}</h1>

              <div className="pdp__pricerow">
                <p className="pdp__price">
                  {formatPrice(price)}
                  {mrp > price && <s className="pdp__mrp">{formatPrice(mrp)}</s>}
                  {off > 0 && <span className="pdp__off">{off}%</span>}
                  <span className="pdp__per">/ {variant ? variant.label : product.unit}</span>
                </p>
                <div className="pdp__quickacts">
                  <button
                    type="button"
                    className={`pdp__iconbtn ${wished ? "is-wished" : ""}`}
                    onClick={toggleWishlist}
                    aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
                    aria-pressed={wished}
                  >
                    <HeartIcon filled={wished} />
                  </button>
                  <button type="button" className="pdp__iconbtn" onClick={share} aria-label="Share this product">
                    <ShareIcon />
                  </button>
                  {shared && <span className="pdp__shared" role="status">Link copied!</span>}
                </div>
              </div>

              <hr className="pdp__rule" />

              {variants ? (
                <div className="pdp__sizes" role="group" aria-label={`Pack size for ${product.name}`}>
                  {variants.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className={`pc__size ${v.id === variant.id ? "is-active" : ""}`}
                      onClick={() => setSizeId(v.id)}
                      aria-pressed={v.id === variant.id}
                    >
                      {v.label}
                      {(items[cartKey(product.id, v.id)] || 0) > 0 && (
                        <span className="pc__size-dot" aria-label="in bag" />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="pdp__unit">Sold per {product.unit}</p>
              )}

              {product.soldOut ? (
                <div className="pdp__soldout" role="status">
                  <strong>Currently out of stock</strong>
                  <span>This is a seasonal item — message us on WhatsApp and we&apos;ll tell you when it&apos;s back.</span>
                </div>
              ) : (
                <div className="pdp__actions">
                  {qty === 0 ? (
                    <button className="pdp__add" onClick={() => add(key)}>+ Add</button>
                  ) : (
                    <div className="pdp__stepper">
                      <button onClick={() => remove(key)} aria-label={`Remove one ${unit}`}>−</button>
                      <span>{qty}</span>
                      <button onClick={() => add(key)} aria-label={`Add one ${unit}`}>+</button>
                    </div>
                  )}
                  <button className="pdp__buynow" onClick={buyNow}>Buy Now</button>
                </div>
              )}

              {qty > 0 && (
                <p className="pdp__inbag">
                  {qty} in your bag · <Link to="/cart">View bag</Link>
                </p>
              )}

              <hr className="pdp__rule" />

              <ul className="pdp__trust">
                {TRUST_BADGES.map((b) => (
                  <li key={b.id}>
                    <span className="pdp__trust-icon">{b.icon}</span>
                    <span>{b.label}</span>
                  </li>
                ))}
              </ul>

              <div className="pdp__delivery">
                <h2 className="pdp__delivery-title">Delivery Options</h2>
                <div className="pdp__delivery-row">
                  <form className="pdp__pin" onSubmit={checkPin}>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="Enter your pincode"
                      value={pin}
                      onChange={(e) => {
                        setPin(e.target.value.replace(/\D/g, ""));
                        setPinStatus(null);
                      }}
                      aria-label="Delivery pincode"
                    />
                    <button type="submit">Check</button>
                  </form>
                  <p className="pdp__delivery-note">
                    <TruckIcon />
                    <span>Get delivery at your doorstep</span>
                  </p>
                </div>
                {pinStatus === "ok" && (
                  <p className="pdp__pin-msg pdp__pin-msg--ok" role="status">
                    ✓ Delivery available to {pin}. Order now for farm-fresh dispatch.
                  </p>
                )}
                {pinStatus === "bad" && (
                  <p className="pdp__pin-msg pdp__pin-msg--bad" role="alert">
                    Please enter a valid 6-digit pincode.
                  </p>
                )}
              </div>

              <hr className="pdp__rule" />

              <div className="pdp__about">
                <h2>{product.name}</h2>
                <p className="pdp__tagline">{product.tag}</p>
                <p className="pdp__desc">
                  Welcome to <strong>Rimmon Farm</strong>, where every harvest comes straight from
                  our fields to your table. {product.desc}
                </p>

                {product.specs?.length > 0 && (
                  <div className="pdp__specs">
                    <h3>Product specifications</h3>
                    <table>
                      <tbody>
                        {product.specs.map((s) => (
                          <tr key={s.label}>
                            <th scope="row">{s.label}</th>
                            <td>{s.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="section section--soft">
          <div className="container">
            <div className="section__head">
              <h2>More from <strong>{categoryLabel}</strong></h2>
            </div>
            <div className="grid">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
